import { useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import type { ClientListItem } from '@kreart/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormContext } from '@/components/form';
import { useFieldError } from '@/components/form/utils';
import { useClient, useClients } from '@/features/clients/hooks';

interface ClientSearchFieldProps {
  name: string;
  label: string;
  placeholder?: string;
  onCreateNew?: () => void;
}

export function ClientSearchField({
  name,
  label,
  placeholder,
  onCreateNew,
}: ClientSearchFieldProps) {
  const { t } = useTranslation();
  const { clearErrors, trigger } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const errorMessage = useFieldError(name);
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [open, setOpen] = useState(false);
  const selectedId = typeof field.value === 'string' ? field.value : '';
  const selectedClientQuery = useClient(selectedId || undefined);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(inputValue.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [inputValue]);

  useEffect(() => {
    if (selectedClientQuery.data && selectedClientQuery.data.id === selectedId) {
      setInputValue(selectedClientQuery.data.name);
    }
  }, [selectedClientQuery.data, selectedId]);

  const clientsQuery = useClients({
    page: 1,
    pageSize: 10,
    search: debouncedSearch || undefined,
    includeDeleted: false,
  });
  const clients = useMemo(() => clientsQuery.data?.items ?? [], [clientsQuery.data?.items]);
  const fieldId = `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;

  const selectClient = (client: ClientListItem) => {
    field.onChange(client.id);
    setInputValue(client.name);
    setOpen(false);
    clearErrors(name);
    window.setTimeout(() => void trigger(name), 0);
  };

  const createNew = () => {
    setOpen(false);
    onCreateNew?.();
  };

  return (
    <div className="relative">
      <Label htmlFor={fieldId}>{label}</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={fieldId}
          value={inputValue}
          placeholder={placeholder}
          className="pl-9"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fieldId}-listbox`}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorMessage ? errorId : undefined}
          onBlur={() => {
            field.onBlur();
            window.setTimeout(() => setOpen(false), 100);
          }}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setInputValue(event.currentTarget.value);
            if (selectedId) field.onChange('');
            setOpen(true);
          }}
        />
      </div>
      {open && (
        <div
          id={`${fieldId}-listbox`}
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-lg"
          onMouseDown={(event) => event.preventDefault()}
        >
          {onCreateNew && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left font-medium text-primary hover:bg-accent"
              onClick={createNew}
            >
              <Plus className="h-4 w-4" />
              {t('projects.clientPicker.newClient')}
            </button>
          )}
          {clientsQuery.isLoading ? (
            <div className="px-3 py-2 text-muted-foreground">{t('common.loading')}</div>
          ) : clients.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">
              {t('projects.clientPicker.noResults')}
            </div>
          ) : (
            clients.map((client) => (
              <button
                key={client.id}
                type="button"
                role="option"
                aria-selected={client.id === selectedId}
                className="block w-full rounded-sm px-3 py-2 text-left hover:bg-accent"
                onClick={() => selectClient(client)}
              >
                <span className="block font-medium">{client.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {[client.company, client.email, client.phone].filter(Boolean).join(' · ') || '—'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
      {errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
